import { enhancedFetch } from '../utils/api-helpers';
import type { Property } from '../types/property';
import { filterZestimateDescriptions } from '../utils';
import type { SoldSearchParams } from './sold-search';
import { PROPERTY_TYPE_API_TO_UI_MAP } from '../utils/property-type-conversion';
import { shouldExcludeFromFlow } from '../utils/status-gating';

// Import centralized caching
import { propertyCache, cacheSoldProperties } from '../utils/centralized-cache';

/**
 * Convert Zillow homeType to our property type format
 * @param homeType - Zillow homeType value (e.g., "TOWNHOUSE", "SINGLE_FAMILY")
 * @returns Converted property type string
 */
function convertHomeTypeToPropertyType(homeType: string): string {
  // Convert Zillow homeType to our API format
  const homeTypeMap: Record<string, string> = {
    'SINGLE_FAMILY': 'singleFamily',
    'TOWNHOUSE': 'townhome',
    'CONDO': 'condo',
    'MULTI_FAMILY': 'multiFamily',
    'MANUFACTURED': 'manufactured',
    'LAND': 'land',
    'LOT': 'land', // LOT maps to land
    'VACANT_LAND': 'land', // VacantLand maps to land
    'APARTMENT': 'apartment',
  };
  
  return homeTypeMap[homeType] || 'singleFamily';
}

/**
 * Normalize address for better Zillow API compatibility
 * @param address - Raw address string
 * @returns Normalized address string
 */
function normalizeAddressForZillow(address: string): string {
  // Remove extra spaces and normalize commas
  let normalized = address.trim().replace(/\s+/g, ' ').replace(/,\s*/g, ', ');
  
  // Ensure proper comma separation between city, state, zip
  const parts = normalized.split(',');
  if (parts.length >= 3) {
    // Format: Street, City, State ZIP
    const street = parts[0].trim();
    const city = parts[1].trim();
    const stateZip = parts[2].trim();
    
    // Check if state and zip are separated
    const stateZipMatch = stateZip.match(/^([A-Z]{2})\s*(\d{5}(?:-\d{4})?)$/);
    if (stateZipMatch) {
      const [, state, zip] = stateZipMatch;
      normalized = `${street}, ${city}, ${state} ${zip}`;
    } else {
      normalized = `${street}, ${city}, ${stateZip}`;
    }
  }
  
  // Expand common abbreviations
  const abbreviations: Record<string, string> = {
    'Dr': 'Drive',
    'St': 'Street',
    'Ave': 'Avenue',
    'Blvd': 'Boulevard',
    'Ln': 'Lane',
    'Ct': 'Court',
    'Pl': 'Place',
    'Cir': 'Circle',
    'Rd': 'Road',
    'Way': 'Way'
  };
  
  // Replace abbreviations in the street part only
  const streetPart = normalized.split(',')[0];
  let updatedStreet = streetPart;
  for (const [abbr, full] of Object.entries(abbreviations)) {
    const regex = new RegExp(`\\b${abbr}\\b`, 'gi');
    updatedStreet = updatedStreet.replace(regex, full);
  }
  
  if (updatedStreet !== streetPart) {
    normalized = normalized.replace(streetPart, updatedStreet);
  }
  
  // Additional Zillow-specific normalizations
  // Remove any trailing punctuation
  normalized = normalized.replace(/[.,;]+$/, '');
  
  // Ensure there's a space between state and zip if missing
  normalized = normalized.replace(/([A-Z]{2})(\d{5})/g, '$1 $2');
  
  // Handle common address formatting issues
  normalized = normalized.replace(/\s*,\s*/g, ', '); // Normalize comma spacing
  normalized = normalized.replace(/\s+/g, ' '); // Normalize multiple spaces
  
  return normalized;
}

/**
 * Create a Property object from address search data
 * This function now uses the same logic as sold-search for consistency
 * @param propertyData - Raw property data from Zillow API
 * @param address - Original address string
 * @returns Property object
 */
function createPropertyFromAddressData(propertyData: any, address: string): Property {
  // Try hard to find a real Zillow property id (zpid)
  const resolvedZpid = String(
    propertyData?.zpid ||
    propertyData?.id ||
    propertyData?.propertyId ||
    propertyData?.homeId ||
    propertyData?.homeInfo?.zpid ||
    propertyData?.metaData?.zpid ||
    ''
  ) || `address-${Date.now()}`;
  // Create a Property object manually to match the structure used in sold-search
  const property: Property = {
    id: resolvedZpid,
    source_id: resolvedZpid,
    title: '', // Don't store titles for sold properties
    address: address,
    city: '',
    state: '',
    zipCode: '',
    price: 0, // Don't store prices for sold properties
    details: {
      bedrooms: 0,
      bathrooms: 0,
      squareFeet: 0,
      yearBuilt: 0,
      lotSize: { amount: 0 }
    },
    type: convertHomeTypeToPropertyType(propertyData.homeType || 'SINGLE_FAMILY'),
    status: 'sold',
    photos: (() => {
      // Prioritize high-resolution photos
      if (propertyData.photoUrlsHighRes && Array.isArray(propertyData.photoUrlsHighRes)) {
        return propertyData.photoUrlsHighRes.map((p: any) => p.url).filter(Boolean);
      }
      return propertyData.photoUrls?.map((p: any) => p.url) || propertyData.photos || [];
    })(),
          description: filterZestimateDescriptions(propertyData.description),
    location: {
      lat: propertyData.latitude || 0,
      lon: propertyData.longitude || 0
    },
    url: propertyData.postingUrl || propertyData.url || '',
    last_sync: new Date().toISOString(),
    source: 'zillow',
    
  };
  
  // Override with address-specific data if available
  if (propertyData.formattedChip?.location) {
    const locationData = propertyData.formattedChip.location;
    if (locationData[0]?.fullValue) {
      property.address = locationData[0].fullValue;
    }
    if (locationData[1]?.fullValue) {
      const cityStateZip = locationData[1].fullValue;
      const parts = cityStateZip.split(', ');
      if (parts.length >= 2) {
        property.city = parts[0];
        const stateZip = parts[1];
        const stateZipMatch = stateZip.match(/^([A-Z]{2})\s*(\d{5}(?:-\d{4})?)$/);
        if (stateZipMatch) {
          property.state = stateZipMatch[1];
          property.zipCode = stateZipMatch[2];
        }
      }
    }
  }

  // Prefer structured address fields when available
  if (propertyData.address) {
    const a = propertyData.address;
    property.address = a.streetAddress || property.address;
    property.city = a.city || property.city;
    property.state = a.state || property.state;
    property.zipCode = a.zipcode || a.zipCode || property.zipCode;
    if (a.latitude && a.longitude) {
      property.location.lat = a.latitude;
      property.location.lon = a.longitude;
    }
  }
  
  // Extract property details from quickFacts if available
  if (propertyData.formattedChip?.quickFacts && Array.isArray(propertyData.formattedChip.quickFacts)) {
    propertyData.formattedChip.quickFacts.forEach((fact: any, index: number) => {
      
              if (fact.elementType === 'beds' && fact.value?.fullValue) {
          property.details!.bedrooms = parseInt(fact.value.fullValue) || 0;
        } else if (fact.elementType === 'baths' && fact.value?.fullValue) {
          property.details!.bathrooms = parseFloat(fact.value.fullValue) || 0;
        } else if (fact.elementType === 'livingArea' && fact.value?.fullValue) {
          // Extract square footage from quickFacts, but prefer livingAreaValue if it's larger
          const quickFactsValue = parseInt(fact.value.fullValue) || 0;
          property.details!.squareFeet = quickFactsValue;
        }
    });
    
  } else {
    // No quickFacts available or not an array
  }
  
  // Extract data from resoFacts if available (often more reliable than quickFacts)
  if (propertyData.resoFacts) {
    if (propertyData.resoFacts.yearBuilt && !property.details!.yearBuilt) {
      property.details!.yearBuilt = parseInt(propertyData.resoFacts.yearBuilt) || 0;
    }
    
    if (propertyData.resoFacts.lotSize && !property.details!.lotSize?.amount) {
      // Parse lot size (e.g., "8,537 sqft" -> 8537, "0.30 Acres" -> 0.30)
      const lotSizeMatch = propertyData.resoFacts.lotSize.match(/(\d+(?:,\d+)*(?:\.\d+)?)/);
      if (lotSizeMatch) {
        const lotSizeNumber = parseFloat(lotSizeMatch[1].replace(/,/g, ''));
        // Round to 2 decimal places like Zillow does
        const roundedValue = Math.round(lotSizeNumber * 100) / 100;
        property.details!.lotSize = { amount: roundedValue };
      }
    }
  }
  
  // Extract data from direct properties if available
  if (propertyData.livingAreaValue) {
    // Always prefer livingAreaValue as it's more reliable than quickFacts
    const livingAreaValue = parseInt(propertyData.livingAreaValue) || 0;
    if (livingAreaValue > 0) {
      property.details!.squareFeet = livingAreaValue;
    }
  }
  // Fallbacks for core facts at top-level
  if (propertyData.bedrooms && !property.details!.bedrooms) {
    property.details!.bedrooms = parseInt(propertyData.bedrooms) || 0;
  }
  if (propertyData.bathrooms && !property.details!.bathrooms) {
    property.details!.bathrooms = parseFloat(propertyData.bathrooms) || 0;
  }
  if (propertyData.livingArea && !property.details!.squareFeet) {
    const area = parseInt(propertyData.livingArea) || 0;
    if (area > 0) property.details!.squareFeet = area;
  }
  
  if (propertyData.lotAreaValue && !property.details!.lotSize?.amount) {
    // Round to 2 decimal places like Zillow does
    const lotSizeValue = parseFloat(propertyData.lotAreaValue) || 0;
    const roundedValue = Math.round(lotSizeValue * 100) / 100;
    property.details!.lotSize = { amount: roundedValue };
  }
  
  // Extract lot area units for proper display
  if (propertyData.lotAreaUnits) {
    // Add lot_area_units to the property object for database upsert
    property.lot_area_units = propertyData.lotAreaUnits;
  }
  
  if (propertyData.yearBuilt && !property.details!.yearBuilt) {
    property.details!.yearBuilt = parseInt(propertyData.yearBuilt) || 0;
  }
  
  // Don't extract price for sold properties - we'll keep it as 0
  
  // Extract sold date and price similar to regular sold search
  let soldDate: string | null = null;
  if (propertyData.formattedChip?.additionalFacts && Array.isArray(propertyData.formattedChip.additionalFacts)) {
    const soldDateFact = propertyData.formattedChip.additionalFacts.find((fact: any) =>
      fact.elementType === 'soldOnDate' && fact.value?.fullValue
    );
    if (soldDateFact) soldDate = soldDateFact.value.fullValue;
  }
  if (!soldDate && propertyData.additionalFacts && Array.isArray(propertyData.additionalFacts)) {
    const soldFact = propertyData.additionalFacts.find((fact: any) =>
      fact.factLabel === 'Sold Date' || fact.factLabel === 'Sold'
    );
    if (soldFact?.value?.fullValue) soldDate = soldFact.value.fullValue;
  }
  let soldPrice: number | null = null;
  if (typeof propertyData.soldPrice === 'number') {
    soldPrice = propertyData.soldPrice;
  } else if (propertyData.soldPrice?.value) {
    soldPrice = Number(propertyData.soldPrice.value) || null;
  } else if (Array.isArray(propertyData.priceHistory)) {
    const soldEvent = propertyData.priceHistory.find((p: any) => (p.event || p.eventType)?.toLowerCase().includes('sold'));
    const rawPrice = soldEvent?.price?.value || soldEvent?.price || soldEvent?.value;
    if (rawPrice) soldPrice = Number(rawPrice) || null;
  }
  (property as any).soldPropertyMetadata = {
    ...(property as any).soldPropertyMetadata,
    soldDate: soldDate || null,
    soldPrice: soldPrice || null,
    lotSize: property.details?.lotSize?.amount || null,
    lotSizeUnit: (property as any).lot_area_units || null,
  };
  if (soldPrice) {
    (property as any).soldInfo = {
      price: soldPrice,
      date: soldDate || '',
      display: `Sold for $${Number(soldPrice).toLocaleString()}${soldDate ? ` on ${soldDate}` : ''}`
    };
  }
  if (soldDate) {
    (property as any).soldDate = soldDate;
    (property as any).sold_date = soldDate;
  }

  return property;
}

/**
 * Try to search for a property using a specific address format
 */
async function tryAddressSearch(addressFormat: string, originalAddress: string): Promise<Property[] | null> {
  // Minimal delay between attempts to avoid rate limiting but keep it fast
  await new Promise(resolve => setTimeout(resolve, 50)); // Reduced from 100ms to 50ms
  
  const searchUrl = '/api/properties/search-address';
  const urlParams = new URLSearchParams();
  urlParams.append('address', addressFormat);
  
  try {
    const response = await enhancedFetch(`${searchUrl}?${urlParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // Check if it's a Zillow validation error
      if (response.status === 400 && errorText.includes('not a detailed address')) {
        return null; // Try next format
      }
      
      // Check for rate limiting
      if (response.status === 429) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return null; // Try next format
      }
      
      throw new Error(`Address search failed: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    
    
    if (!data.data) {
      return null; // Try next format
    }
    
    // Transform the address search result to a Property object
    const propertyData = data.data;
    
    // Guard: exclude non-sold results (e.g., FOR_SALE, FOR_RENT) from sold address flow
    if (shouldExcludeFromFlow(propertyData, 'sold')) {
      return null; // Let other flows (buy/rent) handle it
    }
    
    // The address endpoint already has comprehensive data - no need to call details endpoint
    // Extract data directly from the address endpoint response
    const detailedPropertyData = propertyData;
    

    
    const property: Property = createPropertyFromAddressData(detailedPropertyData, originalAddress);
    

    
    // Cache the result using unified caching for dual cache (raw + resolved location)
    try {
      // Create search params for dual caching
      const searchParamsForCache = {
        location: originalAddress, // Use the original address as resolved location
        status: 'sold',
        page: 0,
        sort: 'relevance'
      };
      cacheSoldProperties([property], originalAddress, undefined);
    } catch (error) {
      // Unified cache failed, fallback to database only
      try {
        // Create minimal search params for fallback caching
        const fallbackSearchParams = {
          location: originalAddress,
          status: 'sold',
          page: 0,
          sort: 'relevance'
        };
        cacheSoldProperties([property], originalAddress, undefined);
      } catch (dbError) {
        // All caching failed, but continue with operation
      }
    }
    
    return [property];
    
  } catch (error) {
    throw error;
  }
}

/**
 * Search sold properties by specific address
 * This function detects if the input is an address and searches accordingly
 */
export const searchSoldPropertiesByAddress = async (
  address: string, 
  searchParams: Partial<SoldSearchParams> = {}, 
  retryCount: number = 0,
  fallbackSearch?: (input: string, params: Partial<SoldSearchParams>) => Promise<Property[]>
): Promise<Property[]> => {
  // searchSoldPropertiesByAddress called for address
  // Retry count, has fallback
  try {
    
    // Use the same cache strategy as sold-search
    const normalizedAddress = normalizeAddressForZillow(address);
    const searchCacheKey = `sold-address-${normalizedAddress.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;
    
    // Check centralized cache first
    const cachedSearch = propertyCache.get(searchCacheKey);
    if (cachedSearch) {
      return cachedSearch;
    }
    
    // Prevent infinite loops
    if (retryCount > 2) {
      if (fallbackSearch) {
        return await fallbackSearch(address, searchParams);
      }
      throw new Error('Max retries reached and no fallback search provided');
    }
    
    // Normalize the address for better Zillow API compatibility
    const normalizedAddressForSearch = normalizeAddressForZillow(address);
    
    // Detect if this is an address-based search (contains street number and address)
    // Pattern: starts with number + space, and contains street-like patterns
    const isAddressSearch = /^\d+\s+/.test(normalizedAddressForSearch.trim()) && 
                           (normalizedAddressForSearch.includes(',') || 
                            /[A-Z]{2}/.test(normalizedAddressForSearch) || 
                            /\b(?:Rd|Road|St|Street|Ave|Avenue|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(normalizedAddressForSearch));
    
    if (!isAddressSearch) {
      if (fallbackSearch) {
        return await fallbackSearch(address, searchParams);
      }
      throw new Error('Input does not appear to be an address and no fallback search provided');
    }
    
    // Try different address formats to improve success rate
    const addressFormats = [
      normalizedAddressForSearch, // Try normalized version first (most likely to work)
      address, // Try original address
      address.replace(/,\s*\d{5}(?:-\d{4})?$/, ''), // Without zip
      address.split(',').slice(0, 2).join(',') // Just street and city
    ];
    
    // Track processed addresses to avoid duplicates
    const processedAddresses = new Set<string>();
    
    // Try each address format
    for (let i = 0; i < addressFormats.length; i++) {
      const currentFormat = addressFormats[i];
      
      // Skip if we've already processed this exact format
      if (processedAddresses.has(currentFormat)) {
        continue;
      }
      
      processedAddresses.add(currentFormat);
      
      try {
        const result = await tryAddressSearch(currentFormat, address);
        if (result) {
          // Cache the successful result using unified caching
          try {
            // Use centralized cache function
            cacheSoldProperties(result, address, undefined);
          } catch (error) {
            // Failed to cache result
          }
          
          // Return results immediately - don't wait for database caching
          return result;
        }
      } catch (error) {
        if (i === addressFormats.length - 1) {
          throw error;
        }
      }
    }
    
    // If all formats fail, fall back to location search
    if (fallbackSearch) {
      return await fallbackSearch(address, searchParams);
    }
    throw new Error('All address formats failed and no fallback search provided');
    
  } catch (error) {
    // Fall back to regular location search if address search fails
    if (fallbackSearch) {
      return await fallbackSearch(address, searchParams);
    }
    throw error;
  }
};



/**
 * Get address suggestions for sold properties
 * This provides autocomplete functionality for address searches
 */
export const getSoldPropertyAddressSuggestions = async (userInput: string): Promise<any[]> => {
  try {
    const autoCompleteUrl = '/api/properties/auto-complete';
    const params = new URLSearchParams();
    params.append('query', userInput);

    const response = await enhancedFetch(
      `${autoCompleteUrl}?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();
    
    
    // Filter for address-type suggestions
    if (data.data && Array.isArray(data.data)) {
      const addressSuggestions = data.data
        .filter((item: any) => item.resultType === "Address" && item.display)
        .map((item: any) => ({
          display: item.display,
          resultType: item.resultType,
          metaData: item.metaData
        }));
      
      return addressSuggestions;
    }

    return [];
  } catch (error) {
    return [];
  }
}; 